import { useCallback, useEffect, useRef } from "react";
import { isSilentCapture, maxAudioPeak } from "./voice/ptt/audioSignal";

export { isSilentCapture, maxAudioPeak } from "./voice/ptt/audioSignal";

export type PttCaptureCallbacks = {
  onRecordingStart: () => void;
  onRecordingStop: (blob: Blob) => void;
  onError: (message: string) => void;
};

const MAX_RECORD_MS = 45_000;
/** MediaRecorder timeslice is 250ms; tail must cover last chunk flush + operator release lag. */
const STOP_TAIL_MS = 1_000;

function flushAndStop(recorder: MediaRecorder): void {
  if (recorder.state === "recording") {
    recorder.requestData();
  }
  if (recorder.state !== "inactive") {
    recorder.stop();
  }
}

function pickMimeType(): string {
  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "";
}

export type PttCaptureHandle = {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  isRecording: () => boolean;
};

/**
 * MediaRecorder microphone capture for PTT blob transport.
 * Keyboard binding lives in voice/ptt/usePttKeyBinding.ts.
 */
export function usePttCapture(callbacks: PttCaptureCallbacks): PttCaptureHandle {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const signalFrameRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const maxAudioPeakRef = useRef(0);
  const signalMonitorAvailableRef = useRef(false);
  const startPendingRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<string>("");

  const stopSignalMonitor = useCallback(() => {
    if (signalFrameRef.current !== null) {
      cancelAnimationFrame(signalFrameRef.current);
      signalFrameRef.current = null;
    }
    audioSourceRef.current?.disconnect();
    analyserRef.current?.disconnect();
    void audioContextRef.current?.close().catch(() => undefined);
    audioSourceRef.current = null;
    analyserRef.current = null;
    audioContextRef.current = null;
  }, []);

  const releaseStream = useCallback((stream: MediaStream | null) => {
    stream?.getTracks().forEach((track) => track.stop());
    if (activeStreamRef.current === stream) {
      activeStreamRef.current = null;
    }
  }, []);

  const abortPendingStart = useCallback(() => {
    startPendingRef.current = false;
    releaseStream(activeStreamRef.current);
    stopSignalMonitor();
  }, [releaseStream, stopSignalMonitor]);

  const startSignalMonitor = useCallback((stream: MediaStream) => {
    const AudioContextCtor =
      window.AudioContext ??
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;

    try {
      const audioContext = new AudioContextCtor();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      const samples = new Float32Array(analyser.fftSize);
      const sampleSignal = () => {
        analyser.getFloatTimeDomainData(samples);
        maxAudioPeakRef.current = Math.max(maxAudioPeakRef.current, maxAudioPeak(samples));
        signalFrameRef.current = requestAnimationFrame(sampleSignal);
      };

      audioContextRef.current = audioContext;
      audioSourceRef.current = source;
      analyserRef.current = analyser;
      signalMonitorAvailableRef.current = true;
      sampleSignal();
    } catch {
      signalMonitorAvailableRef.current = false;
      stopSignalMonitor();
    }
  }, [stopSignalMonitor]);

  const stopRecording = useCallback(() => {
    stopRequestedRef.current = true;
    if (startPendingRef.current || !recorderRef.current) {
      abortPendingStart();
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (stopDelayRef.current) clearTimeout(stopDelayRef.current);
    stopDelayRef.current = setTimeout(() => {
      stopDelayRef.current = null;
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") return;
      flushAndStop(recorder);
    }, STOP_TAIL_MS);
  }, [abortPendingStart]);

  const startRecording = useCallback(async () => {
    if (stopDelayRef.current) {
      clearTimeout(stopDelayRef.current);
      stopDelayRef.current = null;
    }
    if (startPendingRef.current || (recorderRef.current && recorderRef.current.state !== "inactive")) {
      return;
    }
    stopRequestedRef.current = false;
    startPendingRef.current = true;
    stopSignalMonitor();
    startedAtRef.current = 0;
    maxAudioPeakRef.current = 0;
    signalMonitorAvailableRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      startPendingRef.current = false;
      callbacks.onError("Microphone access denied");
      return;
    }

    if (stopRequestedRef.current) {
      releaseStream(stream);
      startPendingRef.current = false;
      return;
    }

    activeStreamRef.current = stream;

    const mimeType = pickMimeType();
    mimeRef.current = mimeType;
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    chunksRef.current = [];
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstart = () => {
      if (stopRequestedRef.current) {
        flushAndStop(recorder);
        return;
      }
      callbacks.onRecordingStart();
    };

    recorder.onstop = () => {
      startPendingRef.current = false;
      stopRequestedRef.current = false;
      stopSignalMonitor();
      releaseStream(stream);
      const mime = mimeRef.current || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      const durationMs = Date.now() - startedAtRef.current;
      recorderRef.current = null;
      if (blob.size === 0) {
        callbacks.onError("No microphone audio was recorded");
        return;
      }
      if (
        signalMonitorAvailableRef.current &&
        isSilentCapture(maxAudioPeakRef.current, durationMs)
      ) {
        callbacks.onError("No microphone signal detected");
        return;
      }
      callbacks.onRecordingStop(blob);
    };

    recorder.onerror = () => {
      callbacks.onError("Recording error");
      stopRecording();
    };

    startedAtRef.current = Date.now();
    startSignalMonitor(stream);
    recorder.start(250);
    startPendingRef.current = false;

    timeoutRef.current = setTimeout(() => {
      stopRecording();
    }, MAX_RECORD_MS);
  }, [callbacks, releaseStream, startSignalMonitor, stopRecording, stopSignalMonitor]);

  const isRecording = useCallback(() => {
    return recorderRef.current?.state === "recording";
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (stopDelayRef.current) clearTimeout(stopDelayRef.current);
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      abortPendingStart();
      stopSignalMonitor();
    };
  }, [abortPendingStart, stopSignalMonitor]);

  return { startRecording, stopRecording, isRecording };
}
